import React, { useEffect, useState, Component } from 'react';
import { ChevronRight, Clock, Trophy, Gift, Star, ChevronLeft, ChevronDown, ChevronUp, ArrowRight, Plus, PlusCircle, Filter, Users, MessageCircle, Heart } from 'lucide-react';
import { PhotoRatingFlow } from './PhotoRatingFlow';
import { PollGenerator } from './polls/PollGenerator';
import { PollFeed } from './polls/PollFeed';
import { PollCard } from './polls/PollCard';
// Poll types
interface PollQuestion {
  id: string;
  text: string;
  options: {
    id: string;
    text: string;
  }[];
}
interface Poll {
  id: string;
  title: string;
  emoji: string;
  description: string;
  reward: number;
  questions: PollQuestion[];
  upsellTitle: string;
  upsellDescription: string;
  upsellCTA: string;
  upsellLink: string;
  upsellImage?: string;
}
// Poll Card Component (original simple version - we'll keep for backwards compatibility)
const SimplePollCard = ({
  poll,
  onTakePoll
}: {
  poll: Poll;
  onTakePoll: (pollId: string) => void;
}) => {
  return <div className="relative w-full bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-900 rounded-xl overflow-hidden shadow-sm border border-purple-100 dark:border-gray-700 hover:shadow-md transition-all" data-id="element-19940">
      <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/80 dark:bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-pink-600 dark:text-pink-400" data-id="element-19941">
        <Gift size={12} data-id="element-19942" />+{poll.reward} LuvTokens
      </div>
      <div className="p-4" data-id="element-19943">
        <div className="text-3xl mb-2" data-id="element-19944">{poll.emoji}</div>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-1" data-id="element-19945">
          {poll.title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-19946">
          {poll.description}
        </p>
        <button onClick={() => onTakePoll(poll.id)} className="w-full py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-1 hover:shadow-lg hover:shadow-pink-500/20 transition-all" data-id="element-19947">
          Take Poll <ChevronRight size={16} data-id="element-19948" />
        </button>
      </div>
    </div>;
};
// Poll Question Component
const PollQuestion = ({
  question,
  onAnswer,
  currentStep,
  totalSteps
}: {
  question: PollQuestion;
  onAnswer: (questionId: string, optionId: string) => void;
  currentStep: number;
  totalSteps: number;
}) => {
  return <div className="w-full max-w-lg mx-auto" data-id="element-19949">
      <div className="mb-2 flex justify-between items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-19950">
        <span data-id="element-19951">
          Question {currentStep} of {totalSteps}
        </span>
        <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-19952">
          <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" style={{
          width: `${currentStep / totalSteps * 100}%`
        }} data-id="element-19953"></div>
        </div>
      </div>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4" data-id="element-19954">
        {question.text}
      </h3>
      <div className="space-y-3" data-id="element-19955">
        {question.options.map(option => <button key={option.id} onClick={() => onAnswer(question.id, option.id)} className="w-full py-3 px-4 text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-pink-50 dark:hover:bg-gray-700 transition-all flex items-center justify-between group" data-id="element-19956">
            <span className="text-gray-800 dark:text-white font-medium" data-id="element-19957">
              {option.text}
            </span>
            <span className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 group-hover:border-pink-500 dark:group-hover:border-pink-500 flex items-center justify-center" data-id="element-19958">
              <span className="w-2.5 h-2.5 rounded-full bg-transparent group-hover:bg-pink-500" data-id="element-19959"></span>
            </span>
          </button>)}
      </div>
    </div>;
};
// Poll Result Component
const PollResult = ({
  poll,
  answers,
  onClaim,
  onNext,
  setShowPhotoRatingFlow
}: {
  poll: Poll;
  answers: Record<string, string>;
  onClaim: () => void;
  onNext: () => void;
  setShowPhotoRatingFlow: (show: boolean) => void;
}) => {
  // Determine result message based on answers (simplified for demo)
  const getResultMessage = () => {
    // This would be more sophisticated in a real implementation
    const randomMessages = ["You're a passionate explorer who knows exactly what you want!", 'Your adventurous spirit makes you irresistible to our angels!', 'You have a unique taste that sets you apart from the crowd!', 'Your desires are intense and focused - our angels love that!'];
    return randomMessages[Math.floor(Math.random() * randomMessages.length)];
  };
  // Check if this is the dickpic challenge poll
  const isDickpicChallenge = poll.upsellLink === 'dickpic_challenge';
  return <div className="w-full max-w-lg mx-auto text-center" data-id="element-19960">
      <div className="mb-5" data-id="element-19961">
        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-4xl mb-3" data-id="element-19962">
          {poll.emoji}
        </div>
        <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2" data-id="element-19963">
          Your Result
        </h3>
        <p className="text-lg text-gray-700 dark:text-gray-300" data-id="element-19964">
          {getResultMessage()}
        </p>
      </div>
      <div className="p-5 mb-5 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-xl border border-purple-100 dark:border-gray-600" data-id="element-19965">
        <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-2" data-id="element-19966">
          {poll.upsellTitle}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-19967">
          {poll.upsellDescription}
        </p>
        {poll.upsellImage && <div className="mb-3 rounded-lg overflow-hidden" data-id="element-19968">
            <img src={poll.upsellImage} alt="Special offer" className="w-full h-40 object-cover" data-id="element-19969" />
          </div>}
        <button onClick={isDickpicChallenge ? () => {
        setShowPhotoRatingFlow(true);
        // Do NOT call onNext() here - that's causing the navigation to GoldenShowerTina
      } : onNext} className="w-full py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-1 hover:shadow-lg hover:shadow-pink-500/20 transition-all" data-id="element-19970">
          {poll.upsellCTA} <ArrowRight size={16} data-id="element-19971" />
        </button>
      </div>
      <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-100 dark:border-green-800/30 flex items-center justify-between" data-id="element-19972">
        <div className="flex items-center gap-2" data-id="element-19973">
          <Gift className="text-green-500" size={20} data-id="element-19974" />
          <span className="font-semibold text-green-600 dark:text-green-400" data-id="element-19975">
            +{poll.reward} LuvTokens
          </span>
        </div>
        <button onClick={onClaim} className="px-4 py-1.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-green-500/20 transition-all" data-id="element-19976">
          Claim Now
        </button>
      </div>
    </div>;
};
// Main Social Polls Section Component
export const SocialPollsSection = () => {
  const [activeTab, setActiveTab] = useState<'available' | 'completed' | 'create' | 'community'>('available');
  const [activePollId, setActivePollId] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [pollAnswers, setPollAnswers] = useState<Record<string, Record<string, string>>>({});
  const [completedPolls, setCompletedPolls] = useState<string[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [pollStreak, setPollStreak] = useState(3); // Mock streak data
  const [tokenBalance, setTokenBalance] = useState(240); // Mock token balance
  const [showPhotoRatingFlow, setShowPhotoRatingFlow] = useState(false);
  const [showPollGenerator, setShowPollGenerator] = useState(false);
  const [ritualId, setRitualId] = useState<string | undefined>(undefined);
  const [communityFilter, setCommunityFilter] = useState<'all' | 'ritual'>('all');
  // Sample poll data
  const polls: Poll[] = [{
    id: 'lover_type',
    title: 'What Kind of Lover Are You?',
    emoji: '❤️',
    description: 'Discover your lover archetype and find your perfect match!',
    reward: 25,
    questions: [{
      id: 'q1',
      text: 'Do you prefer to tease or please?',
      options: [{
        id: 'o1',
        text: "I'm all about the tease"
      }, {
        id: 'o2',
        text: 'Pleasing is my priority'
      }, {
        id: 'o3',
        text: 'Equal balance of both'
      }]
    }, {
      id: 'q2',
      text: 'Your ideal pace is...',
      options: [{
        id: 'o1',
        text: 'Slow and sensual'
      }, {
        id: 'o2',
        text: 'Wild and passionate'
      }, {
        id: 'o3',
        text: 'Start slow, end wild'
      }]
    }, {
      id: 'q3',
      text: 'Do you prefer to give or receive?',
      options: [{
        id: 'o1',
        text: 'I love to give'
      }, {
        id: 'o2',
        text: 'I prefer to receive'
      }, {
        id: 'o3',
        text: 'Equal exchange'
      }]
    }],
    upsellTitle: "You're a Passionate Giver!",
    upsellDescription: 'Our angels love your style. Deepen your connection with premium relationship features.',
    upsellCTA: 'Build a deeper bond with your angel now',
    upsellLink: 'relationship_tracking'
  }, {
    id: 'kink_vanilla',
    title: 'Kink or Vanilla?',
    emoji: '🍦😈',
    description: "Where do you fall on the spectrum? Let's find out!",
    reward: 25,
    questions: [{
      id: 'q1',
      text: 'How do you feel about light BDSM?',
      options: [{
        id: 'o1',
        text: 'Love it!'
      }, {
        id: 'o2',
        text: "Curious but haven't tried"
      }, {
        id: 'o3',
        text: 'Not my thing'
      }]
    }, {
      id: 'q2',
      text: 'Do you enjoy roleplay scenarios?',
      options: [{
        id: 'o1',
        text: 'Yes, all the time'
      }, {
        id: 'o2',
        text: 'Sometimes'
      }, {
        id: 'o3',
        text: 'Rarely or never'
      }]
    }, {
      id: 'q3',
      text: 'How adventurous are you with toys?',
      options: [{
        id: 'o1',
        text: 'Very - the more the better'
      }, {
        id: 'o2',
        text: 'I have a few favorites'
      }, {
        id: 'o3',
        text: 'Not really into toys'
      }]
    }],
    upsellTitle: "You're More Kinky Than 75% of Users!",
    upsellDescription: 'Get matched with angels who share your adventurous spirit and unlock exclusive kink content.',
    upsellCTA: 'Get matched with kinky angels',
    upsellLink: 'games'
  }, {
    id: 'dickpic_decoder',
    title: 'Dickpic Decoder',
    emoji: '🍆🔍',
    description: 'How confident are you? Take this poll and find out!',
    reward: 25,
    questions: [{
      id: 'q1',
      text: 'How confident are you about your size?',
      options: [{
        id: 'o1',
        text: 'Very confident'
      }, {
        id: 'o2',
        text: 'Somewhat confident'
      }, {
        id: 'o3',
        text: 'Not very confident'
      }]
    }, {
      id: 'q2',
      text: "What's your grooming style?",
      options: [{
        id: 'o1',
        text: 'Completely smooth'
      }, {
        id: 'o2',
        text: 'Neatly trimmed'
      }, {
        id: 'o3',
        text: 'Natural'
      }]
    }, {
      id: 'q3',
      text: 'How often have you sent a dickpic?',
      options: [{
        id: 'o1',
        text: 'Frequently'
      }, {
        id: 'o2',
        text: 'A few times'
      }, {
        id: 'o3',
        text: 'Never'
      }]
    }],
    upsellTitle: "You're Ready for a Professional Opinion!",
    upsellDescription: 'Get honest feedback and tips from your favorite angels with 20% off your first analysis.',
    upsellCTA: 'Let her rate yours today',
    upsellLink: 'dickpic_challenge',
    upsellImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png'
  }];
  // Handle poll creation
  const handleCreatePoll = () => {
    setShowPollGenerator(true);
  };
  const handlePollCreated = (pollData: any) => {
    console.log('Poll created:', pollData);
    // In a real app, you would update your state or fetch the updated polls
    setShowPollGenerator(false);
    // Show success message
    const successEvent = new CustomEvent('showAlert', {
      detail: {
        message: 'Poll created successfully! Users can now vote on it.',
        type: 'success'
      }
    });
    document.dispatchEvent(successEvent);
    // Switch to community tab to see the new poll
    setActiveTab('community');
    // Update ritual progress if this was created as part of a ritual
    if (pollData.ritualId) {
      const ritualEvent = new CustomEvent('ritualPollCreated', {
        detail: {
          ritualId: pollData.ritualId,
          pollId: pollData.id
        }
      });
      document.dispatchEvent(ritualEvent);
    }
  };
  // Handle taking a poll
  const handleTakePoll = (pollId: string) => {
    setActivePollId(pollId);
    setCurrentQuestionIndex(0);
    setShowResult(false);
  };
  // Handle answering a question
  const handleAnswer = (questionId: string, optionId: string) => {
    if (!activePollId) return;
    setPollAnswers(prev => ({
      ...prev,
      [activePollId]: {
        ...(prev[activePollId] || {}),
        [questionId]: optionId
      }
    }));
    const activePoll = polls.find(p => p.id === activePollId);
    if (!activePoll) return;
    if (currentQuestionIndex < activePoll.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setShowResult(true);
    }
  };
  // Handle claiming rewards
  const handleClaimReward = () => {
    if (!activePollId) return;
    const activePoll = polls.find(p => p.id === activePollId);
    if (!activePoll) return;
    // Update completed polls
    setCompletedPolls(prev => [...prev, activePollId]);
    // Add tokens to balance
    setTokenBalance(prev => prev + activePoll.reward);
    // Show success message or notification
    const claimEvent = new CustomEvent('showAlert', {
      detail: {
        message: `+${activePoll.reward} LuvTokens added to your balance!`,
        type: 'success'
      }
    });
    document.dispatchEvent(claimEvent);
    // Reset to available polls view
    setActivePollId(null);
  };
  // Handle upsell navigation
  const handleUpsellNavigation = () => {
    if (!activePollId) return;
    const activePoll = polls.find(p => p.id === activePollId);
    if (!activePoll) return;
    // Navigate to the appropriate section based on the upsell link
    const navigateEvent = new CustomEvent('navigate', {
      detail: {
        section: activePoll.upsellLink
      }
    });
    document.dispatchEvent(navigateEvent);
    // Reset to available polls view
    setActivePollId(null);
  };
  // Filter available and completed polls
  const availablePolls = polls.filter(poll => !completedPolls.includes(poll.id));
  const completedPollsData = polls.filter(poll => completedPolls.includes(poll.id));
  // Get active poll if one is selected
  const activePoll = activePollId ? polls.find(p => p.id === activePollId) : null;
  const activeQuestion = activePoll && currentQuestionIndex < activePoll.questions.length ? activePoll.questions[currentQuestionIndex] : null;
  // Calculate time until next polls
  const [timeUntilNextPolls, setTimeUntilNextPolls] = useState('15h 34m');
  // Update time every minute (in a real app)
  useEffect(() => {
    // This would be a real countdown in production
    const interval = setInterval(() => {
      // Mock countdown logic
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  // Add event listener for opening photo rating flow
  useEffect(() => {
    const handleOpenPhotoRatingFlow = () => {
      setShowPhotoRatingFlow(true);
    };
    document.addEventListener('openPhotoRatingFlow', handleOpenPhotoRatingFlow);
    return () => {
      document.removeEventListener('openPhotoRatingFlow', handleOpenPhotoRatingFlow);
    };
  }, []);
  // Listen for ritual-related events
  useEffect(() => {
    const handleRitualPoll = (event: any) => {
      if (event.detail) {
        // If the ritual requires creating a poll
        if (event.detail.action === 'create') {
          setRitualId(event.detail.ritualId);
          setShowPollGenerator(true);
        }
        // If the ritual requires voting on polls
        else if (event.detail.action === 'vote') {
          setRitualId(event.detail.ritualId);
          setActiveTab('community');
          setCommunityFilter('ritual');
        }
      }
    };
    document.addEventListener('ritualPoll', handleRitualPoll);
    return () => {
      document.removeEventListener('ritualPoll', handleRitualPoll);
    };
  }, []);
  return <div className="w-full max-w-4xl mx-auto" data-id="element-19977">
      {/* Header Section */}
      <div className="mb-6 text-center" data-id="element-19978">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center gap-2 mb-2" data-id="element-19979">
          🗳️ Social Polls{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500" data-id="element-19980">
            – What's Your Fantasy?
          </span>
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-4" data-id="element-19981">
          Take a naughty poll, get rewarded 💎
        </p>
        {/* Streak Tracker */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-full border border-amber-100 dark:border-amber-800/30 text-amber-700 dark:text-amber-300 mb-5" data-id="element-19982">
          <Trophy size={16} className="text-amber-500" data-id="element-19983" />
          <span className="font-medium" data-id="element-19984">{pollStreak}-Day Poll Streak:</span>
          <span className="font-semibold text-green-600 dark:text-green-400" data-id="element-19985">
            +15 bonus tokens tomorrow
          </span>
        </div>
        {/* Create Poll Button - Prominently displayed */}
        <div className="mb-5" data-id="element-19986">
          <button onClick={handleCreatePoll} className="px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium text-sm hover:shadow-lg hover:shadow-pink-500/20 transition-all flex items-center gap-2 mx-auto" data-id="element-19987">
            <PlusCircle size={18} data-id="element-19988" />
            <span data-id="element-19989">Create Your Own Poll</span>
          </button>
        </div>
        {/* Available Polls Counter */}
        <div className="flex items-center justify-center gap-4 mb-2" data-id="element-19990">
          <div className="flex items-center gap-1.5" data-id="element-19991">
            <span className="w-3 h-3 rounded-full bg-pink-500" data-id="element-19992"></span>
            <span className="text-sm font-medium" data-id="element-19993">
              {availablePolls.length} Available
            </span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-19994">
            <span className="w-3 h-3 rounded-full bg-green-500" data-id="element-19995"></span>
            <span className="text-sm font-medium" data-id="element-19996">
              {completedPolls.length} Completed
            </span>
          </div>
        </div>
      </div>

      {/* If a poll is active, show questions or results */}
      {activePollId && activePoll ? <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6" data-id="element-19997">
          {!showResult && activeQuestion ? <PollQuestion question={activeQuestion} onAnswer={handleAnswer} currentStep={currentQuestionIndex + 1} totalSteps={activePoll.questions.length} data-id="element-19998" /> : <PollResult poll={activePoll} answers={pollAnswers[activePollId] || {}} onClaim={handleClaimReward} onNext={handleUpsellNavigation} setShowPhotoRatingFlow={setShowPhotoRatingFlow} data-id="element-19999" />}
        </div> : <>
          {/* Tabs */}
          <div className="flex items-center justify-center mb-5 overflow-x-auto pb-2 scrollbar-hide" data-id="element-20000">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-full p-1 flex" data-id="element-20001">
              <button onClick={() => setActiveTab('available')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeTab === 'available' ? 'bg-white dark:bg-gray-700 text-pink-600 dark:text-pink-400 shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-pink-500 dark:hover:text-pink-400'}`} data-id="element-20002">
                Available Polls
              </button>
              <button onClick={() => setActiveTab('community')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeTab === 'community' ? 'bg-white dark:bg-gray-700 text-pink-600 dark:text-pink-400 shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-pink-500 dark:hover:text-pink-400'}`} data-id="element-20003">
                Community Polls
              </button>
              <button onClick={() => setActiveTab('completed')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeTab === 'completed' ? 'bg-white dark:bg-gray-700 text-pink-600 dark:text-pink-400 shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-pink-500 dark:hover:text-pink-400'}`} data-id="element-20004">
                Completed Polls
              </button>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'available' ?
      // Original poll cards
      availablePolls.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-20005">
                {availablePolls.map(poll => <SimplePollCard key={poll.id} poll={poll} onTakePoll={handleTakePoll} data-id="element-20006" />)}
              </div> : <div className="text-center py-10 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800" data-id="element-20007">
                <div className="text-4xl mb-3" data-id="element-20008">🎉</div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2" data-id="element-20009">
                  All Polls Completed!
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-20010">
                  You've completed all available polls for today.
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-700 dark:text-gray-300 mb-5" data-id="element-20011">
                  <Clock size={16} data-id="element-20012" />
                  <span data-id="element-20013">New polls in {timeUntilNextPolls}</span>
                </div>
                <button className="px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium text-sm hover:shadow-lg hover:shadow-pink-500/20 transition-all" data-id="element-20014">
                  Unlock Tomorrow's Polls Early (50 LuvTokens)
                </button>
              </div> : activeTab === 'community' ?
      // Community polls section with PollFeed
      <div data-id="element-20015">
              <div className="mb-4 flex items-center justify-between" data-id="element-20016">
                <div className="flex items-center gap-2" data-id="element-20017">
                  <button onClick={() => setCommunityFilter('all')} className={`px-3 py-1.5 rounded-lg text-sm ${communityFilter === 'all' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} data-id="element-20018">
                    All Polls
                  </button>
                  <button onClick={() => setCommunityFilter('ritual')} className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${communityFilter === 'ritual' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} data-id="element-20019">
                    <Trophy size={14} data-id="element-20020" />
                    <span data-id="element-20021">Ritual Polls</span>
                  </button>
                </div>
                <button onClick={handleCreatePoll} className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700" data-id="element-20022">
                  <PlusCircle size={16} data-id="element-20023" />
                  <span className="text-sm" data-id="element-20024">New Poll</span>
                </button>
              </div>
              <PollFeed showRitualPollsOnly={communityFilter === 'ritual'} onCreatePoll={handleCreatePoll} ritualId={ritualId} data-id="element-20025" />
            </div> : completedPollsData.length > 0 ?
      // Completed polls section
      <div className="space-y-4" data-id="element-20026">
              {completedPollsData.map(poll => <div key={poll.id} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 flex items-center" data-id="element-20027">
                  <div className="text-2xl mr-3" data-id="element-20028">{poll.emoji}</div>
                  <div className="flex-1" data-id="element-20029">
                    <h3 className="font-medium text-gray-800 dark:text-white" data-id="element-20030">
                      {poll.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-20031">
                      Completed today
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-green-500 font-medium" data-id="element-20032">
                    <Gift size={16} data-id="element-20033" />
                    <span data-id="element-20034">+{poll.reward}</span>
                  </div>
                </div>)}
              {/* Poll History Stats */}
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl border border-purple-100 dark:border-purple-800/30" data-id="element-20035">
                <h3 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2" data-id="element-20036">
                  <Trophy size={16} className="text-amber-500" data-id="element-20037" />
                  Your Poll Achievements
                </h3>
                <div className="space-y-2" data-id="element-20038">
                  <div className="flex items-center justify-between" data-id="element-20039">
                    <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-20040">
                      Current Streak
                    </span>
                    <span className="font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1" data-id="element-20041">
                      <Trophy size={14} data-id="element-20042" />
                      {pollStreak} days
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-20043">
                    <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-20044">
                      Total Polls Taken
                    </span>
                    <span className="font-medium" data-id="element-20045">
                      {completedPolls.length + 12}
                    </span>{' '}
                    {/* Mock data */}
                  </div>
                  <div className="flex items-center justify-between" data-id="element-20046">
                    <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-20047">
                      Tokens Earned
                    </span>
                    <span className="font-medium text-green-600 dark:text-green-400" data-id="element-20048">
                      325 LuvTokens
                    </span>{' '}
                    {/* Mock data */}
                  </div>
                  <div className="flex items-center justify-between" data-id="element-20049">
                    <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-20050">
                      Poll Rank
                    </span>
                    <span className="font-medium text-purple-600 dark:text-purple-400 flex items-center gap-1" data-id="element-20051">
                      <Star size={14} data-id="element-20052" />
                      Poll Prince
                    </span>
                  </div>
                </div>
              </div>
            </div> : <div className="text-center py-10 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800" data-id="element-20053">
              <div className="text-4xl mb-3" data-id="element-20054">📊</div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2" data-id="element-20055">
                No Completed Polls
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-20056">
                Take your first poll to earn LuvTokens!
              </p>
              <button onClick={() => setActiveTab('available')} className="px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium text-sm hover:shadow-lg hover:shadow-pink-500/20 transition-all" data-id="element-20057">
                View Available Polls
              </button>
            </div>}

          {/* Top Polls Section */}
          {activeTab === 'available' && availablePolls.length > 0 && <div className="mt-8" data-id="element-20058">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2" data-id="element-20059">
                🔥 Top Polls Today
              </h2>
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-20060">
                {/* Poll 1 */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-20061">
                  <div className="flex items-center justify-between" data-id="element-20062">
                    <div className="flex items-center gap-2" data-id="element-20063">
                      <span className="text-xl" data-id="element-20064">😈</span>
                      <h3 className="font-medium text-gray-800 dark:text-white" data-id="element-20065">
                        Kink or Vanilla?
                      </h3>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-20066">
                      2.4k votes
                    </span>
                  </div>
                  <div className="mt-3 space-y-2" data-id="element-20067">
                    <div data-id="element-20068">
                      <div className="flex items-center justify-between text-sm mb-1" data-id="element-20069">
                        <span data-id="element-20070">Love light BDSM</span>
                        <span className="font-medium" data-id="element-20071">68%</span>
                      </div>
                      <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-20072">
                        <div className="h-full bg-pink-500 rounded-full" style={{
                    width: '68%'
                  }} data-id="element-20073"></div>
                      </div>
                    </div>
                    <div data-id="element-20074">
                      <div className="flex items-center justify-between text-sm mb-1" data-id="element-20075">
                        <span data-id="element-20076">Curious but haven't tried</span>
                        <span className="font-medium" data-id="element-20077">22%</span>
                      </div>
                      <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-20078">
                        <div className="h-full bg-purple-500 rounded-full" style={{
                    width: '22%'
                  }} data-id="element-20079"></div>
                      </div>
                    </div>
                    <div data-id="element-20080">
                      <div className="flex items-center justify-between text-sm mb-1" data-id="element-20081">
                        <span data-id="element-20082">Not my thing</span>
                        <span className="font-medium" data-id="element-20083">10%</span>
                      </div>
                      <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-20084">
                        <div className="h-full bg-blue-500 rounded-full" style={{
                    width: '10%'
                  }} data-id="element-20085"></div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Poll 2 */}
                <div className="p-4" data-id="element-20086">
                  <div className="flex items-center justify-between" data-id="element-20087">
                    <div className="flex items-center gap-2" data-id="element-20088">
                      <span className="text-xl" data-id="element-20089">🍆</span>
                      <h3 className="font-medium text-gray-800 dark:text-white" data-id="element-20090">
                        Dickpic Confidence
                      </h3>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-20091">
                      1.8k votes
                    </span>
                  </div>
                  <div className="mt-3 space-y-2" data-id="element-20092">
                    <div data-id="element-20093">
                      <div className="flex items-center justify-between text-sm mb-1" data-id="element-20094">
                        <span data-id="element-20095">Very confident</span>
                        <span className="font-medium" data-id="element-20096">42%</span>
                      </div>
                      <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-20097">
                        <div className="h-full bg-pink-500 rounded-full" style={{
                    width: '42%'
                  }} data-id="element-20098"></div>
                      </div>
                    </div>
                    <div data-id="element-20099">
                      <div className="flex items-center justify-between text-sm mb-1" data-id="element-20100">
                        <span data-id="element-20101">Somewhat confident</span>
                        <span className="font-medium" data-id="element-20102">45%</span>
                      </div>
                      <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-20103">
                        <div className="h-full bg-purple-500 rounded-full" style={{
                    width: '45%'
                  }} data-id="element-20104"></div>
                      </div>
                    </div>
                    <div data-id="element-20105">
                      <div className="flex items-center justify-between text-sm mb-1" data-id="element-20106">
                        <span data-id="element-20107">Not very confident</span>
                        <span className="font-medium" data-id="element-20108">13%</span>
                      </div>
                      <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-20109">
                        <div className="h-full bg-blue-500 rounded-full" style={{
                    width: '13%'
                  }} data-id="element-20110"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </>}

      {/* Missed Yesterday Section */}
      {activeTab === 'available' && !activePollId && <div className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30" data-id="element-20111">
          <div className="flex items-center justify-between" data-id="element-20112">
            <div data-id="element-20113">
              <h3 className="font-semibold text-gray-800 dark:text-white mb-1" data-id="element-20114">
                Missed Yesterday?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-20115">
                Restore your poll streak and get back on track!
              </p>
            </div>
            <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-blue-500/20 transition-all" data-id="element-20116">
              Restore Streak (30 LuvTokens)
            </button>
          </div>
        </div>}

      {/* Poll Generator Modal */}
      {showPollGenerator && <PollGenerator onClose={() => setShowPollGenerator(false)} onPollCreated={handlePollCreated} ritualId={ritualId} data-id="element-20117" />}

      {/* PhotoRatingFlow Modal */}
      {showPhotoRatingFlow && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-20118">
          <PhotoRatingFlow onClose={() => setShowPhotoRatingFlow(false)} data-id="element-20119" />
        </div>}
    </div>;
};